"use client";

import { forwardRef, useState, useCallback, useRef, useEffect } from "react";
import { Input, type InputProps } from "./input";

export type DateInputProps = Omit<InputProps, "onChange" | "value" | "type"> & {
  value?: string;
  onChange?: (value: string) => void;
};

/**
 * A date input component that allows typing dates in mm/dd/yyyy format.
 * Only accepts numeric input and automatically formats with slashes.
 * Returns the date in YYYY-MM-DD format for API compatibility.
 * 
 * Composes the existing Input component for consistent styling.
 */
const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ value = "", onChange, placeholder = "mm/dd/yyyy", ...props }, ref) => {
    const formatForDisplay = (isoDate: string): string => {
      if (!isoDate) return "";
      const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
      const match = dateRegex.exec(isoDate);
      if (match) {
        const [, year, month, day] = match;
        return `${month}/${day}/${year}`;
      }
      return isoDate;
    };

    const formatForApi = (displayDate: string): string => {
      const digitsOnly = displayDate.replace(/\D/g, "");
      if (digitsOnly.length === 8) {
        const month = digitsOnly.slice(0, 2);
        const day = digitsOnly.slice(2, 4);
        const year = digitsOnly.slice(4, 8);
        return `${year}-${month}-${day}`;
      }
      return "";
    };

    const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value));
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPositionRef = useRef<number>(0);

    useEffect(() => {
      const newDisplayValue = formatForDisplay(value);
      if (newDisplayValue !== displayValue) {
        setDisplayValue(newDisplayValue);
      }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const formatWithSlashes = useCallback((digits: string): string => {
      let formatted = "";
      for (let i = 0; i < digits.length && i < 8; i++) {
        if (i === 2 || i === 4) formatted += "/";
        formatted += digits[i];
      }
      return formatted;
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const rawValue = input.value;
      const cursorPos = input.selectionStart ?? 0;
      
      const digitsOnly = rawValue.replace(/\D/g, "");
      const limitedDigits = digitsOnly.slice(0, 8);
      const formatted = formatWithSlashes(limitedDigits);
      
      const oldDigitsBeforeCursor = displayValue.slice(0, cursorPositionRef.current).replace(/\D/g, "").length;
      const newDigitsBeforeCursor = rawValue.slice(0, cursorPos).replace(/\D/g, "").length;
      const digitDiff = newDigitsBeforeCursor - oldDigitsBeforeCursor;
      
      let newCursorPos = cursorPos;
      if (digitDiff > 0) {
        const digitsTyped = newDigitsBeforeCursor;
        if (digitsTyped === 3 || digitsTyped === 5) {
          newCursorPos = cursorPos + 1;
        }
      }
      
      newCursorPos = Math.min(newCursorPos, formatted.length);
      
      setDisplayValue(formatted);
      cursorPositionRef.current = newCursorPos;
      
      if (onChange) {
        onChange(formatForApi(formatted));
      }
      
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    }, [displayValue, formatWithSlashes, onChange]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      cursorPositionRef.current = input.selectionStart ?? 0;
      
      const allowedKeys = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
      if (allowedKeys.includes(e.key)) return;
      
      if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
      
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
        return;
      }
      
      const currentDigits = displayValue.replace(/\D/g, "");
      if (currentDigits.length >= 8 && input.selectionStart === input.selectionEnd) {
        e.preventDefault();
      }
    }, [displayValue]);

    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");
      const digitsOnly = pastedText.replace(/\D/g, "");
      
      if (digitsOnly.length > 0) {
        const input = e.currentTarget;
        const selStart = input.selectionStart ?? 0;
        const selEnd = input.selectionEnd ?? 0;
        
        const digitsBefore = displayValue.slice(0, selStart).replace(/\D/g, "");
        const digitsAfter = displayValue.slice(selEnd).replace(/\D/g, "");
        
        const newDigits = (digitsBefore + digitsOnly + digitsAfter).slice(0, 8);
        const formatted = formatWithSlashes(newDigits);
        
        setDisplayValue(formatted);
        
        if (onChange) {
          onChange(formatForApi(formatted));
        }
        
        const newCursorDigits = Math.min(digitsBefore.length + digitsOnly.length, 8);
        let newCursorPos = newCursorDigits;
        if (newCursorDigits > 2) newCursorPos++;
        if (newCursorDigits > 4) newCursorPos++;
        
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          }
        });
      }
    }, [displayValue, formatWithSlashes, onChange]);

    return (
      <Input
        ref={(node) => {
          (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder}
        {...props}
      />
    );
  }
);

DateInput.displayName = "DateInput";

export { DateInput };
